import type { LogLevel, LogMeta, LoggerOptions } from './types'
import { shouldLog } from './levels'
import { formatLog } from './formatters'
import { getState } from './state'
import { handleSentry, addBreadcrumb } from './sentry'
import { handleElastic } from './elastic'
import { redactMeta, redactString } from './redact'

// Internal keys that should not be forwarded as console meta
const INTERNAL_KEYS = new Set(['sentry', 'index', 'error', 'skipPiiMasking'])

function cleanMeta(meta?: LogMeta): Record<string, unknown> | undefined {
  if (!meta) return undefined
  const cleaned: Record<string, unknown> = {}
  let hasKeys = false
  for (const key of Object.keys(meta)) {
    if (!INTERNAL_KEYS.has(key)) {
      cleaned[key] = meta[key]
      hasKeys = true
    }
  }
  return hasKeys ? cleaned : undefined
}

export class Logger {
  private ns: string
  private options?: LoggerOptions

  constructor(namespace: string, options?: LoggerOptions) {
    this.ns = namespace
    this.options = options
  }

  debug(message: string, meta?: LogMeta): void {
    this.log('debug', message, meta)
  }

  info(message: string, meta?: LogMeta): void {
    this.log('info', message, meta)
  }

  warn(message: string, meta?: LogMeta): void {
    this.log('warn', message, meta)
  }

  error(message: string, meta?: LogMeta): void {
    this.log('error', message, meta)
  }

  setUser(user: { id?: string; email?: string; [key: string]: unknown }): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setUser(user)
    }
  }

  setTags(tags: Record<string, string>): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setTags(tags)
    }
  }

  setExtras(extras: Record<string, unknown>): void {
    const state = getState()
    if (state.sentry) {
      state.sentry.setExtras(extras)
    }
  }

  private log(level: LogLevel, message: string, meta?: LogMeta): void {
    const state = getState()

    // Determine whether PII masking should be skipped for this call.
    // Secrets are ALWAYS masked regardless of this flag.
    const skipPii = meta?.skipPiiMasking === true || this.options?.maskPII === false || state.maskPII === false

    // Mask secrets (always) and PII (unless bypassed) for console and Sentry.
    // ES receives the raw form so stored data can be replayed without corruption.
    const safeMessage = redactString(message, skipPii)
    const safeMeta = redactMeta(meta, skipPii)

    // Sentry breadcrumb for info, warn, error (regardless of log level filtering)
    if (level !== 'debug') {
      try {
        addBreadcrumb(state.sentry, level, this.ns, safeMessage, cleanMeta(safeMeta))
      } catch {
        // never let Sentry failures break callers
      }
    }

    // Sentry event if sentry: true
    if (safeMeta?.sentry) {
      try {
        handleSentry(state.sentry, level, safeMessage, safeMeta)
      } catch {
        // never let Sentry failures break callers
      }
    }

    // ES tracking index if index: true — uses raw message/meta so stored data is never masked
    if (safeMeta?.index) {
      handleElastic(state.elastic, state.provider, state.stage, state.version, this.ns, level, message, meta)
    }

    // Console output respects log level
    if (!shouldLog(level, state.level)) {
      return
    }

    const displayMeta = cleanMeta(safeMeta)
    const formatted = formatLog(level, this.ns, safeMessage, displayMeta)

    switch (level) {
      case 'debug':
        console.debug(formatted)
        break
      case 'info':
        console.log(formatted)
        break
      case 'warn':
        console.warn(formatted)
        break
      case 'error':
        console.error(formatted)
        if (safeMeta?.error) {
          console.error(safeMeta.error)
        }
        break
    }
  }
}

export function createLogger(namespace: string, options?: LoggerOptions): Logger {
  return new Logger(namespace, options)
}
